// Lib imports
import _ from 'lodash/fp';

// Core imports
import GraniteError from 'granite-admin/utils/granite-error';

// Application imports
import { uploadToS3 } from 'granite-admin/common/controllers/common';
import permissionGrpMgtAPI from 'granite-admin/common/gateways/permissionGrpMgt';
import organisationAPI from 'granite-admin/organisations/gateways/organisation-api';
import commonApi from 'granite-admin/common/gateways/common-api.js';
import { getBAdminsRoles, createBAdminsRole, editBAdminsRole } from 'granite-admin/common/gateways/business-admin-api';
import { ONBOARDING_EVENTS, ROLE_EVENTS, PERMISSION_EVENTS, ORGANISATION_EVENTS, EMPLOYEE_LIST_EVENTS } from './constants';

// TODO: update comment when migrating to Granite API
/**
 * OnBoarding Page - Implementation of user createOrganisation action.
 *
 * @param {Object} organisation data
 * @param {string} organisation.name
 * @param {string} organisation.description
 * @param {string} organisation.address
 *
 * @returns {json} - a object containing success/failure message
 * @throws {GraniteError} if name is invalid.
 */

async function createOrganizationOnListing(eventEmitter, {
  firstName,
  lastName,
  name,
  email,
  phone,
  location,
  address,
  companyType
}) {
  if (_.isNil(name)) {
    throw new GraniteError('Organization name is invalid');
  }
  try {
    const values = {
      name,
      email,
      phone: `+${phone.number}`,
      location,
      user: {
        first_name: firstName,
        last_name: lastName,
        email,
        country_code: phone.countryCode,
        phone: `+${phone.number}`
      },
      industry: companyType,
      extra_data: {
        address
      }
    };
    const data = await organisationAPI.createOrganizationOnListing(values);
    eventEmitter.emit(ONBOARDING_EVENTS.ONBOARDING_SUCCESS, data);
  } catch (error) {
    eventEmitter.emit(ONBOARDING_EVENTS.ONBOARDING_FAILURE);
  }
}
async function createOrganisationClicked(eventEmitter, {
  name,
  email,
  phone,
  industry,
  domain
}) {
  if (_.isNil(name)) {
    throw new GraniteError('Organization name is invalid');
  }
  try {
    const values = {
      name,
      email,
      country_code: phone.countryCode,
      phone: `+${phone.number}`,
      industry,
      domain
    };
    const data = await organisationAPI.createOrganisation(values);
    eventEmitter.emit(ONBOARDING_EVENTS.ONBOARDING_SUCCESS, data);
  } catch (error) {
    eventEmitter.emit(ONBOARDING_EVENTS.ONBOARDING_FAILURE);
  }
}
async function updateOrganisationClicked(eventEmitter, values) {
  try {
    const response = await organisationAPI.updateOrganisation(values);
    eventEmitter.emit(ORGANISATION_EVENTS.PROFILE_SAVE_SUCCESS, response);
  } catch (error) {
    eventEmitter.emit(ORGANISATION_EVENTS.PROFILE_SAVE_FAILURE);
    throw error;
  }
}
/**
 * Roles Page - Implementation of user getRoles action.
 *
 * @param {Object} eventEmitter
 * @param {Object} an object containing organisationId and params
 *
 * @returns {Object} - an array containing role list
 * @throws {GraniteError} if organisationId is invalid.
 */
async function getPermissions(eventEmitter, businessAdmin) {
  // console.log('REACHED CONTROLLER');
  try {
    // let response;
    // if (businessAdmin) response = await organisationAPI.getAdminPermissions();
    // else response = await organisationAPI.getPermissions();
    // const { permissions } = response;
    // let parsedPermissions = parsePermissions(permissions);
    const response = await permissionGrpMgtAPI.getLogicalPermission({});
    eventEmitter.emit(PERMISSION_EVENTS.LIST_SUCCESS, {
      permissions: response.list
    });
  } catch (error) {
    let errorMessage = JSON.stringify(error);
    if (error.response) errorMessage = error.response.data.message;
    if (error.message) errorMessage = error.message;
    eventEmitter.emit(PERMISSION_EVENTS.LIST_FAILURE);
    throw new GraniteError(errorMessage);
  }
}
async function getAllPermissions(eventEmitter, params) {
  try {
    const response = await organisationAPI.getAdminPermissions(params);
    let parsedPermissions = parsePermissions(response?.permissions);
    eventEmitter.emit(PERMISSION_EVENTS.LIST_SUCCESS, {
      permissions: parsedPermissions
    });
  } catch (error) {
    let errorMessage = JSON.stringify(error);
    if (error.response) errorMessage = error.response.data.message;
    if (error.message) errorMessage = error.message;
    eventEmitter.emit(PERMISSION_EVENTS.LIST_FAILURE);
    throw new GraniteError(errorMessage);
  }
}
export const parsePermissions = array => {
  let response = [];
  array.forEach(item => {
    let existingCategoryPermission = response.find(i => i.category === item.displaySection);
    if (!existingCategoryPermission) {
      response.push({
        category: item.displaySection,
        permissions: [{
          name: item.displayText,
          read: item.accessType === 'READ' ? item.pk : null,
          write: item.accessType === 'WRITE' ? item.pk : null,
          delete: item.accessType === 'DELETE' ? item.pk : null
        }]
      });
    } else if (existingCategoryPermission) {
      let existingNamePermission = existingCategoryPermission.permissions.find(x => x.name === item.displayText);
      if (!existingNamePermission) {
        existingCategoryPermission.permissions.push({
          name: item.displayText,
          read: item.accessType === 'READ' ? item.pk : null,
          write: item.accessType === 'WRITE' ? item.pk : null,
          delete: item.accessType === 'DELETE' ? item.pk : null
        });
      } else if (existingNamePermission) {
        if (item.accessType === 'READ') {
          existingNamePermission.read = item.pk;
        } else if (item.accessType === 'WRITE') {
          existingNamePermission.write = item.pk;
        } else if (item.accessType === 'DELETE') {
          existingNamePermission.delete = item.pk;
        }
      }
    }
  });
  return response;
};
async function fetchData(eventEmitter, businessAdmin) {
  try {
    await Promise.all([getRoles(eventEmitter, businessAdmin), getPermissions(eventEmitter, businessAdmin)]);
    eventEmitter.emit(ROLE_EVENTS.STOP_LOADER);
  } catch (error) {
    console.log(error);
    eventEmitter.emit(ROLE_EVENTS.STOP_LOADER);
  }
}

/**
 * Roles Page - Implementation of user getRoles action.
 *
 * @param {Object} eventEmitter
 * @param {Object} an object containing organisationId and params
 *
 * @returns {Object} - an array containing role list
 * @throws {GraniteError} if organisationId is invalid.
 */
async function getRoles(eventEmitter, businessAdmin) {
  try {
    let response;
    if (businessAdmin) {
      response = await getBAdminsRoles({
        ordering: 'name'
      });
    } else {
      response = await organisationAPI.getRoles();
    }
    // const { roles, count } = await organisationAPI.getRoles();
    eventEmitter.emit(ROLE_EVENTS.GET_SUCCESS, {
      roles: response.roles || response.list,
      count: response.count
    });
    return response.roles || response.list;
  } catch (error) {
    let errorMessage = JSON.stringify(error);
    if (error.response) errorMessage = error.response.data.message;
    if (error.message) errorMessage = error.message;
    eventEmitter.emit(ROLE_EVENTS.GET_FAILURE);
    throw new GraniteError(errorMessage);
  }
}
async function getRolesByOrganisation(organisation) {
  try {
    const data = await organisationAPI.getRolesByOrganisation(organisation);
    return data;
  } catch (error) {
    let errorMessage = JSON.stringify(error);
    if (error.response) errorMessage = error.response.data.message;
    if (error.message) errorMessage = error.message;
    throw new GraniteError(errorMessage);
  }
}
async function getAdminOrganisations(organisation) {
  try {
    const data = await organisationAPI.getAdminOrganisations(organisation);
    return data;
  } catch (error) {
    let errorMessage = JSON.stringify(error);
    if (error.response) errorMessage = error.response.data.message;
    if (error.message) errorMessage = error.message;
    throw new GraniteError(errorMessage);
  }
}

/**
 * Roles Page - Implementation of user createOrganisation action.
 *
 * @param {Object} organisation data
 * @param {string} organisation.name
 *
 * @returns {json} - a object containing success/failure message
 * @throws {GraniteError} if name is invalid.
 */
async function createRole(eventEmitter, {
  name
}, selectRole, businessAdmin) {
  if (_.isNil(name)) {
    throw new GraniteError('Organization name is invalid');
  }
  try {
    const values = {
      name: name
    };
    let data, roles;
    if (businessAdmin) {
      data = await createBAdminsRole(values);
    } else {
      data = await organisationAPI.createRole(values);
    }
    roles = await getRoles(eventEmitter, businessAdmin);
    // const data = await organisationAPI.createRole(values);
    // let roles = await getRoles(eventEmitter);
    let newRole = roles.find(item => +item.pk === +data.pk);
    selectRole(newRole.pk);
    eventEmitter.emit(ROLE_EVENTS.ROLE_CREATION_SUCCESS, data);
  } catch (error) {
    eventEmitter.emit(ROLE_EVENTS.ROLE_CREATION_FAILURE);
    throw error;
  }
}

/**
 * Roles Page - Implementation of user edit role action.
 *
 * @param {Object} role data
 * @param {string} role.name
 *
 * @returns {json} - a object containing success/failure message
 * @throws {GraniteError} if name is invalid.
 */
async function editRole(eventEmitter, {
  roleToEdit,
  name,
  permissions
}, type, businessAdmin) {
  let pk = roleToEdit.pk;
  try {
    let data;
    if (businessAdmin) {
      data = await editBAdminsRole(pk, name, permissions, type);
    } else {
      data = await organisationAPI.editRole(pk, name, permissions, type);
    }
    await getRoles(eventEmitter, businessAdmin);
    // const data = await organisationAPI.editRole(pk, name, permissions, type);
    // await getRoles(eventEmitter);
    eventEmitter.emit(ROLE_EVENTS.ROLE_EDIT_SUCCESS, data);
  } catch (error) {
    let errorMessage = JSON.stringify(error);
    if (error.response) errorMessage = error.response.data.message;
    if (error.message) errorMessage = error.message;
    eventEmitter.emit(ROLE_EVENTS.ROLE_EDIT_FAILURE);
    throw new GraniteError(errorMessage);
  }
}
async function deleteRole(eventEmitter, pk) {
  try {
    await organisationAPI.deleteRole(pk);
    eventEmitter.emit(ROLE_EVENTS.ROLE_DELETE_SUCCESS);
  } catch (error) {
    eventEmitter.emit(ROLE_EVENTS.ROLE_DELETE_FAILURE, {
      title: error?.errors?.title
    });
  }
}
async function getSettings(eventEmitter) {
  try {
    const response = await organisationAPI.getSettings();
    return response;
  } catch (error) {
    eventEmitter.emit(ORGANISATION_EVENTS.SETTINGS_FETCH_FAILURE);
    throw error;
  }
}
async function updateSettings(eventEmitter, values) {
  try {
    await Promise.all(values.map(async val => await organisationAPI.updateSettings(val.pk, val.value)));
    eventEmitter.emit(ORGANISATION_EVENTS.SETTINGS_SAVE_SUCCESS);
  } catch (error) {
    eventEmitter.emit(ORGANISATION_EVENTS.SETTINGS_SAVE_FAILURE);
    throw error;
  }
}

/**
 * Profile Settings Tab - Implementation of getting master config.
 * @throws {GraniteError} if api failed.
 */
async function getMasterPreferences() {
  try {
    const list = await commonApi.getMasterConfigs('ORGANISATION_SETTING');
    return list;
  } catch (error) {
    throw new GraniteError(error.errors);
  }
}
async function sendInvitationEmail(eventEmitter, {
  email
}) {
  const {
    sendInvitationEmail
  } = organisationAPI;
  if (_.isNil(email)) {
    throw new GraniteError('Email is required');
  }
  try {
    await sendInvitationEmail(email);
    eventEmitter.emit(EMPLOYEE_LIST_EVENTS.INVITE_SUCCESS);
  } catch (error) {
    eventEmitter.emit(EMPLOYEE_LIST_EVENTS.INVITE_FAILURE);
  }
}
async function updateOrgLogo(eventEmitter, values) {
  try {
    const file = await uploadToS3(values, organisationAPI.updateOrganisation);
    eventEmitter.emit(ORGANISATION_EVENTS.PROFILE_SAVE_SUCCESS, file);
    return file;
  } catch (err) {
    console.log(err.errors);
    eventEmitter.emit(ORGANISATION_EVENTS.PROFILE_SAVE_FAILURE);
  }
}
export { createOrganisationClicked, createOrganizationOnListing, getAllPermissions, updateOrganisationClicked, getRoles, createRole, editRole, deleteRole, getPermissions, getSettings, updateSettings, getMasterPreferences, fetchData, sendInvitationEmail, getRolesByOrganisation, getAdminOrganisations, updateOrgLogo };